import { useState, useEffect, useRef } from 'react'
import { X, Copy, Share2, Check, AlertTriangle } from 'lucide-react'
import { useLeagueDetail } from '../queries'
import type { League } from '../types'
import styles from './InviteSheet.module.css'

type InviteSheetProps = {
  league:   League
  leagueId: string
  onClose:  () => void
}

export function InviteSheet({ league, leagueId, onClose }: InviteSheetProps) {
  const [copied, setCopied] = useState(false)
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)

  const { data: detail, isLoading: codeLoading } = useLeagueDetail(leagueId)
  const joinCode = detail?.joinCode ?? league.joinCode

  const isFull   = league.memberCount >= league.maxParticipants
  const shareUrl = `https://fantyx.pro/entrar?code=${joinCode}`

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  useEffect(() => {
    sheetRef.current?.focus()
  }, [])

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(joinCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select the code text
    }
  }

  async function handleShare() {
    if (navigator.share) {
      await navigator.share({
        title: `Entre na liga: ${league.name}`,
        text: `Use o código ${joinCode} para entrar na liga "${league.name}" no Fantyx.Pro!`,
        url: shareUrl,
      })
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Convidar membros">
      <div
        ref={sheetRef}
        className={styles.sheet}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.title}>Convidar membros</span>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            <X size={16} />
          </button>
        </div>

        <div className={styles.body}>
          {isFull ? (
            <div className={styles.fullNotice}>
              <AlertTriangle size={18} />
              <p>Liga cheia — limite de {league.maxParticipants} participantes atingido.</p>
            </div>
          ) : (
            <>
              <p className={styles.hint}>
                Compartilhe o código abaixo com quem você quer convidar.
              </p>

              {/* Join code */}
              <div className={styles.codeBlock}>
                <span className={styles.code}>
                  {codeLoading && !joinCode ? '···-····' : joinCode}
                </span>
                <button
                  className={styles.copyBtn}
                  onClick={handleCopy}
                  disabled={!joinCode}
                  aria-label="Copiar código"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>

              {/* Share buttons */}
              <button className={styles.shareBtn} onClick={handleShare}>
                <Share2 size={16} />
                Compartilhar convite
              </button>

              <p className={styles.membersCount}>
                {league.memberCount} de {league.maxParticipants} participantes
              </p>
            </>
          )}

          {/* Regenerate code */}
          {!showRegenerateWarning ? (
            <button
              className={styles.regenerateLink}
              onClick={() => setShowRegenerateWarning(true)}
            >
              Regenerar código de convite
            </button>
          ) : (
            <div className={styles.regenerateWarning}>
              <AlertTriangle size={14} />
              <div>
                <p className={styles.regenerateText}>
                  Isso invalidará o código atual. Qualquer convite já compartilhado deixará de funcionar.
                </p>
                <div className={styles.regenerateActions}>
                  <button
                    className={styles.regenerateConfirm}
                    onClick={() => {
                      // TODO: POST /v1/leagues/{id}/invite-code
                      setShowRegenerateWarning(false)
                    }}
                  >
                    Confirmar
                  </button>
                  <button
                    className={styles.regenerateCancel}
                    onClick={() => setShowRegenerateWarning(false)}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
